import { Component, OnInit, inject } from '@angular/core';
import { ScorecardDisplayType, ScorecardFacade, ScorecardV2, ScorecardV2Query } from '@fgb/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ScorecardsV2Tab } from './components/scorecards-v2-sub-nav/scorecards-v2-sub-nav.component';

/** An active scorecard with the next scorecard in its chain, shown locked inside its card. */
export interface ScorecardsV2Group {
  parent: ScorecardV2;
  child?: ScorecardV2;
}

@Component({
  selector: 'fgb-scorecards-v2-page',
  templateUrl: './scorecards-v2-page.component.html',
  styleUrl: './scorecards-v2-page.component.scss',
})
export class ScorecardsV2PageComponent implements OnInit {
  private scorecardFacade = inject(ScorecardFacade);
  private scorecardQuery = inject(ScorecardV2Query);

  /** True once the scorecards have loaded, so the empty state does not flash while loading. */
  readonly isLoaded = this.scorecardQuery.isLoaded;

  activeTab: ScorecardsV2Tab = 'active';
  activeMobileTab: 'challenges' | 'badges' = 'challenges';
  activeScorecardGroups$: Observable<ScorecardsV2Group[]>;
  completedScorecards$: Observable<ScorecardV2[]>;

  ngOnInit(): void {
    this.scorecardFacade.fetchScorecards();

    const scorecards$ = this.scorecardQuery.selectScorecards({
      filterBy: (s: ScorecardV2) => s.ScoreCardDisplayType !== ScorecardDisplayType.streakView,
    });

    this.activeScorecardGroups$ = scorecards$.pipe(
      map((scorecards) => this.buildActiveGroups(scorecards.filter((s) => !s.Completed)))
    );
    this.completedScorecards$ = scorecards$.pipe(map((scorecards) => scorecards.filter((s) => s.Completed && !s.Locked)));
  }

  onTabChange(tab: ScorecardsV2Tab): void {
    this.activeTab = tab;
  }

  /** Pairs each scorecard with its child (by ParentId), so a locked child is shown inside its parent's card instead of on its own. */
  private buildActiveGroups(scorecards: ScorecardV2[]): ScorecardsV2Group[] {
    const groups: ScorecardsV2Group[] = [];
    const usedIds = new Set<number>();

    for (const scorecard of scorecards) {
      if (usedIds.has(scorecard.ScorecardId)) continue;
      if (scorecard.Locked && scorecard.ParentId) continue;

      usedIds.add(scorecard.ScorecardId);
      const group: ScorecardsV2Group = { parent: scorecard };

      const child = scorecards.find((c) => c.ParentId === scorecard.ScorecardId);
      if (child) {
        group.child = child;
        usedIds.add(child.ScorecardId);
      }

      groups.push(group);
    }

    return groups;
  }

  onPageTabChange(tab: 'challenges' | 'badges'): void {
    this.activeMobileTab = tab;
  }
}
